import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/sellerApi";

const WEIGHTS = ["Under 1kg", "1-5kg", "5-10kg", "10-20kg"];

// Zone center coordinates (matching server/src/utils/pricing.js)
const ZONE_CENTERS = {
  CBD: { lat: -17.8292, lng: 31.0522, label: "Harare CBD" },
  AVONDALE: { lat: -17.796, lng: 31.034, label: "Avondale / Sam Levy's" },
  EASTLEA_MSASA: { lat: -17.825, lng: 31.085, label: "Eastlea / Msasa" },
  BORROWDALE: { lat: -17.765, lng: 31.071, label: "Borrowdale / Pomona" },
};

const SAME_ZONE_DEFAULT_KM = 3;

// Haversine formula with 1.4x Harare road multiplier (mirrors server)
function calcDistance(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const straightLine = R * c;
  return Math.round(straightLine * 1.4 * 100) / 100;
}

// Calculate fee (mirrors server pricing logic)
function calcFee(distanceKm, isExpress, monthlyCount) {
  let baseFee;
  if (distanceKm <= 5) baseFee = 2.0;
  else if (distanceKm <= 10) baseFee = 3.5;
  else if (distanceKm <= 15) baseFee = 5.0;
  else if (distanceKm <= 25) baseFee = 7.0;
  else baseFee = 7.0 + (distanceKm - 25) * 0.4;

  if (isExpress) baseFee += 1.5;

  let discountRate = 0;
  if (monthlyCount >= 81) discountRate = 0.15;
  else if (monthlyCount >= 31) discountRate = 0.1;

  const discountAmt = baseFee * discountRate;
  const finalFee = baseFee - discountAmt;

  return {
    baseFee: Math.round(baseFee * 100) / 100,
    discount: Math.round(discountAmt * 100) / 100,
    discountPercent: discountRate * 100,
    finalFee: Math.round(finalFee * 100) / 100,
  };
}

function calcCodCommission(codAmount) {
  if (!codAmount || codAmount <= 0) return 0;
  return Math.max(Math.round(codAmount * 0.05 * 100) / 100, 0.5);
}

function distanceBandLabel(km) {
  if (km <= 5) return "0–5 km";
  if (km <= 10) return "5–10 km";
  if (km <= 15) return "10–15 km";
  if (km <= 25) return "15–25 km";
  return `${Math.round(km)} km`;
}

export default function NewOrder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    pickup_address:
      user?.seller?.saved_address || user?.seller?.location_description || "",
    buyer_name: "",
    buyer_phone: "",
    dropoff_address: "",
    dropoff_zone: "",
    item_description: "",
    weight_estimate_kg: "Under 1kg",
    special_instructions: "",
    express: false,
    cod_enabled: false,
    cod_amount: "",
  });

  const set = (f) => (e) =>
    setForm((p) => ({
      ...p,
      [f]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  // Auto-calculate distance from seller zone to drop-off zone
  const sellerZone = user?.seller?.zone?.toUpperCase() || "";
  const dropoffZone = form.dropoff_zone;

  let calculatedDistance = null;
  if (dropoffZone && ZONE_CENTERS[dropoffZone]) {
    if (sellerZone === dropoffZone) {
      calculatedDistance = SAME_ZONE_DEFAULT_KM;
    } else if (ZONE_CENTERS[sellerZone]) {
      const from = ZONE_CENTERS[sellerZone];
      const to = ZONE_CENTERS[dropoffZone];
      calculatedDistance = calcDistance(from.lat, from.lng, to.lat, to.lng);
    } else {
      // Seller zone not recognized, default same-zone
      calculatedDistance = SAME_ZONE_DEFAULT_KM;
    }
  }

  // Calculate fee preview whenever inputs change
  const [feePreview, setFeePreview] = useState(null);

  useEffect(() => {
    if (calculatedDistance && calculatedDistance > 0) {
      const monthlyCount = user?.monthly_delivery_count || 0;
      const fee = calcFee(calculatedDistance, form.express, monthlyCount);

      let codComm = 0;
      if (form.cod_enabled && form.cod_amount) {
        codComm = calcCodCommission(parseFloat(form.cod_amount));
      }

      setFeePreview({
        ...fee,
        codCommission: codComm,
        distance: calculatedDistance,
      });
    } else {
      setFeePreview(null);
    }
  }, [
    calculatedDistance,
    form.express,
    form.cod_enabled,
    form.cod_amount,
    user,
  ]);

  const submitOrder = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        ...form,
        distance_km: calculatedDistance,
      };
      const res = await api.post("/orders", payload);
      navigate(`/seller/order/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create order.");
    } finally {
      setSubmitting(false);
    }
  };

  const canNext = () => {
    if (step === 1) return form.pickup_address;
    if (step === 2)
      return (
        form.buyer_name &&
        form.buyer_phone &&
        form.dropoff_address &&
        form.dropoff_zone
      );
    if (step === 3) return true;
    if (step === 4) return true;
    return true;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-green-600 text-white px-5 pt-5 pb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => (step > 1 ? setStep(step - 1) : navigate("/seller"))}
            className="text-green-200"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 className="font-bold text-lg">Request Delivery</h1>
        </div>
        {/* Progress dots */}
        <div className="flex gap-2 mt-3 justify-center">
          {[1, 2, 3, 4, 5].map((s) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all ${s === step ? "w-8 bg-white" : s < step ? "w-4 bg-white/60" : "w-4 bg-white/30"}`}
            />
          ))}
        </div>
        {step >= 2 && feePreview && (
          <div className="mt-3 text-xs text-green-100 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 bg-green-700/50 py-1.5 px-3 rounded-lg border border-green-500/30">
            <span className="font-semibold text-white">
              Estimated: ${feePreview.finalFee.toFixed(2)}
            </span>
            <span>• {distanceBandLabel(feePreview.distance)}</span>
            {form.express && <span>• Express +$1.50</span>}
            {feePreview.discountPercent > 0 && (
              <span>• {feePreview.discountPercent}% off applied</span>
            )}
          </div>
        )}
      </div>

      <div className="px-5 py-6">
        {error && (
          <div className="bg-red-50 text-red-700 text-sm px-4 py-2 rounded-xl mb-4">
            {error}
          </div>
        )}

        {/* Step 1: Pickup */}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Pickup location</h2>
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Pickup address
              </label>
              <textarea
                value={form.pickup_address}
                onChange={set("pickup_address")}
                rows={2}
                className="w-full rounded-xl border-gray-300 text-sm"
                placeholder="Your shop/stall address"
              />
            </div>
            {user?.seller?.zone && (
              <div className="bg-green-50 rounded-xl px-4 py-3">
                <p className="text-xs text-green-700">
                  Your registered zone:{" "}
                  <span className="font-bold">
                    {ZONE_CENTERS[user.seller.zone.toUpperCase()]?.label ||
                      user.seller.zone}
                  </span>
                </p>
              </div>
            )}
            {user?.seller?.saved_address &&
              form.pickup_address !== user.seller.saved_address && (
                <button
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      pickup_address: user.seller.saved_address,
                    }))
                  }
                  className="text-sm text-green-600 font-medium"
                >
                  Use my saved address
                </button>
              )}
          </div>
        )}

        {/* Step 2: Dropoff */}
        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">
              Delivery details
            </h2>
            <input
              value={form.buyer_name}
              onChange={set("buyer_name")}
              placeholder="Buyer name"
              required
              className="w-full rounded-xl border-gray-300 py-3 text-sm"
            />
            <input
              type="tel"
              value={form.buyer_phone}
              onChange={set("buyer_phone")}
              placeholder="Buyer phone number"
              required
              className="w-full rounded-xl border-gray-300 py-3 text-sm"
            />
            <textarea
              value={form.dropoff_address}
              onChange={set("dropoff_address")}
              rows={2}
              placeholder="Drop-off address"
              required
              className="w-full rounded-xl border-gray-300 text-sm"
            />
            <div>
              <label className="block text-sm text-gray-600 mb-2">
                Drop-off zone
              </label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(ZONE_CENTERS).map(([key, zone]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() =>
                      setForm((p) => ({ ...p, dropoff_zone: key }))
                    }
                    className={`py-3 rounded-xl text-sm font-medium border transition-colors ${
                      form.dropoff_zone === key
                        ? "bg-green-50 border-green-500 text-green-700"
                        : "bg-white border-gray-200 text-gray-600"
                    }`}
                  >
                    {zone.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Show auto-calculated distance preview */}
            {calculatedDistance && (
              <div className="bg-blue-50 rounded-xl px-4 py-3 flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-blue-500 shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-blue-700">
                  Estimated distance:{" "}
                  <span className="font-bold">{calculatedDistance} km</span>
                  {sellerZone === dropoffZone && " (same zone)"}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Package */}
        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Package details</h2>
            <input
              value={form.item_description}
              onChange={set("item_description")}
              placeholder="What are you sending?"
              className="w-full rounded-xl border-gray-300 py-3 text-sm"
            />
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Weight estimate
              </label>
              <div className="grid grid-cols-2 gap-2">
                {WEIGHTS.map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() =>
                      setForm((p) => ({ ...p, weight_estimate_kg: w }))
                    }
                    className={`py-2.5 rounded-xl text-sm font-medium border ${
                      form.weight_estimate_kg === w
                        ? "bg-green-50 border-green-500 text-green-700"
                        : "bg-white border-gray-200 text-gray-600"
                    }`}
                  >
                    {w}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              value={form.special_instructions}
              onChange={set("special_instructions")}
              rows={2}
              placeholder="Special instructions (e.g., fragile, keep upright)"
              className="w-full rounded-xl border-gray-300 text-sm"
            />
          </div>
        )}

        {/* Step 4: Options */}
        {step === 4 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">
              Delivery options
            </h2>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <label className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Express delivery</p>
                  <p className="text-sm text-gray-500">
                    Guaranteed pickup within 30 minutes
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">+$1.50</span>
                  <input
                    type="checkbox"
                    checked={form.express}
                    onChange={set("express")}
                    className="rounded border-gray-300 text-green-600 w-5 h-5"
                  />
                </div>
              </label>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <label className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Cash on delivery</p>
                  <p className="text-sm text-gray-500">
                    Rider collects payment from buyer
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={form.cod_enabled}
                  onChange={set("cod_enabled")}
                  className="rounded border-gray-300 text-green-600 w-5 h-5"
                />
              </label>
              {form.cod_enabled && (
                <div className="mt-3">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="200"
                    value={form.cod_amount}
                    onChange={set("cod_amount")}
                    placeholder="Amount to collect (max $200)"
                    className="w-full rounded-lg border-gray-300 text-sm"
                  />
                </div>
              )}
            </div>

            {/* Live fee preview on options step */}
            {feePreview && (
              <div className="bg-gray-50 rounded-xl p-4 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Estimated distance</span>
                  <span className="text-gray-700">
                    {feePreview.distance} km
                  </span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span className="text-gray-700">Estimated fee</span>
                  <span className="text-gray-900">
                    ${feePreview.finalFee.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 5: Confirm */}
        {step === 5 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Confirm order</h2>
            <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
              <div className="p-3.5">
                <p className="text-xs text-gray-500 uppercase">Pickup</p>
                <p className="text-sm text-gray-900 mt-0.5">
                  {form.pickup_address}
                </p>
              </div>
              <div className="p-3.5">
                <p className="text-xs text-gray-500 uppercase">Deliver to</p>
                <p className="text-sm font-medium text-gray-900 mt-0.5">
                  {form.buyer_name} ({form.buyer_phone})
                </p>
                <p className="text-sm text-gray-600">{form.dropoff_address}</p>
                {form.dropoff_zone && ZONE_CENTERS[form.dropoff_zone] && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    Zone: {ZONE_CENTERS[form.dropoff_zone].label}
                  </p>
                )}
              </div>
              <div className="p-3.5">
                <p className="text-xs text-gray-500 uppercase">Package</p>
                <p className="text-sm text-gray-900 mt-0.5">
                  {form.item_description || "Not specified"} (
                  {form.weight_estimate_kg})
                </p>
                {form.special_instructions && (
                  <p className="text-sm text-orange-600">
                    {form.special_instructions}
                  </p>
                )}
              </div>
            </div>

            {/* Fee breakdown */}
            {feePreview ? (
              <div className="bg-green-50 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Estimated distance</span>
                  <span className="font-medium">{feePreview.distance} km</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Base fee</span>
                  <span>${feePreview.baseFee.toFixed(2)}</span>
                </div>
                {form.express && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Express surcharge</span>
                    <span>included</span>
                  </div>
                )}
                {feePreview.discount > 0 && (
                  <div className="flex justify-between text-green-700">
                    <span>Volume discount ({feePreview.discountPercent}%)</span>
                    <span>-${feePreview.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t border-green-200 pt-2 text-base">
                  <span>Delivery fee</span>
                  <span>${feePreview.finalFee.toFixed(2)}</span>
                </div>
                {form.cod_enabled && feePreview.codCommission > 0 && (
                  <p className="text-xs text-gray-500">
                    COD commission: ${feePreview.codCommission.toFixed(2)}{" "}
                    (deducted from collection)
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-yellow-50 rounded-xl p-4 text-sm text-yellow-700">
                Please select a drop-off zone to see the fee estimate.
              </div>
            )}

            <p className="text-xs text-gray-500 text-center">
              {form.cod_enabled
                ? "Delivery fee will be deducted from COD collection"
                : "You will be invoiced for the delivery fee"}
            </p>
          </div>
        )}

        {/* Navigation */}
        <div className="mt-6">
          {step < 5 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canNext()}
              className="w-full bg-green-600 text-white py-3.5 rounded-xl font-bold text-lg disabled:opacity-40"
            >
              Continue
            </button>
          ) : (
            <button
              onClick={submitOrder}
              disabled={submitting || !feePreview}
              className="w-full bg-green-600 text-white py-3.5 rounded-xl font-bold text-lg disabled:opacity-50"
            >
              {submitting ? "Placing order..." : "Confirm Delivery Request"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
